import { Button, Chip, Grid, useTheme, Box, Typography, Divider } from "@mui/material";
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import PrintIcon from '@mui/icons-material/Print';
import Modal from "../../components/Modal/Modal";
import SimpleBackdrop from "../../components/Loading/SimpleBackdrop";
import { useState, useEffect } from "react";
import { ContainerTreeItem, CustomTreeItem } from "../../components/TreeView/TreeView";
import { ClienteType } from "../../types/ClienteType";
import { ContasReceberType } from "../../types/ContasReceberType";
import AccountBoxIcon from '@mui/icons-material/AccountBox';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ListaClientes from "../../modals/clientes/ListaClientes";
import InputLabel from "../../components/Inputs/InputLabel";
import SelectSimple from "../../components/Selects/SelectSimple";
import { AlertConfirm, AlertInfo } from "../../components/Alerts/Alert";
import { api } from "../../services/api";
import { converterMoeda, displayMoney } from "../../utilities/displayValues";
import { formatDate } from "../../utilities/date";
import moment from "moment";

type ModalContaReceberProps = {
    open: boolean,
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    contaReceber: ContasReceberType | null,
    setContaReceber: React.Dispatch<React.SetStateAction<ContasReceberType | null>>
    reload: () => void,
}

export default function ModalContaReceber({ open, setOpen, contaReceber, setContaReceber, reload }: ModalContaReceberProps) {
    const [infoCliente, setInfoCliente] = useState<ClienteType | null>(null);
    const [openLoad, setOpenLoad] = useState(false);
    const [openModalBuscaCliente, setOpenModalBuscaCliente] = useState(false);
    const [openModalPagamento, setOpenModalPagamento] = useState(false);
    const [data_vencimento, setDataVencimento] = useState('');
    const [valor_origem, setValorOrigem] = useState('');
    const [detalhes, setDetalhes] = useState('');
    const [tipo_conta, setTipoConta] = useState<string | number>('BOLETO');
    const [parcelas, setParcelas] = useState<string>('2');
    const [tipo_valor, setTipoValor] = useState<string | number>('TOTAL');
    const [data_pagamento, setDataPagamento] = useState(formatDate(new Date(), "usd"));
    const [valor_pago, setValorPago] = useState('');
    const [parcelasCarnet, setParcelasCarnet] = useState<ContasReceberType[]>([]);
    const theme = useTheme();

    function resetForm() {
        setInfoCliente(null);
        setDataVencimento('');
        setValorOrigem('');
        setDetalhes('');
        setTipoConta('BOLETO');
        setParcelas('2');
        setTipoValor('TOTAL');
        setDataPagamento(formatDate(new Date(), "usd"));
        setValorPago('');
        setParcelasCarnet([]);
        setContaReceber(null);
    }

    function selectCliente() {
        if (!contaReceber) setOpenModalBuscaCliente(true);
    }

    async function abrirCarnetPdf(installmentId: string) {
        setOpenLoad(true);
        try {
            const pdfResponse = await api.get(`/carnetpdf/${installmentId}`, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao obter PDF do carnê' });
        }
        setOpenLoad(false);
    }

    async function handleGerarRecibo() {
        if (!contaReceber || openLoad) return;
        setOpenLoad(true);
        try {
            const pdfResponse = await api.get(`/reciboboleto/${contaReceber.id_receber}`, { responseType: 'blob' });
            const blob = new Blob([pdfResponse.data], { type: 'application/pdf' });
            const blobUrl = URL.createObjectURL(blob);
            window.open(blobUrl, '_blank');
            setTimeout(() => URL.revokeObjectURL(blobUrl), 10000);
        } catch {
            AlertInfo({ icon: 'error', title: 'Falha ao gerar recibo' });
        }
        setOpenLoad(false);
    }

    function getSituacaoChip(cod: number) {
        if (cod === 1) return <Chip label="RECEBIDO" color="success" sx={{ width: '100%' }} />;
        if (cod === 2) return <Chip label="EM ATRASO" color="error" sx={{ width: '100%' }} />;
        if (cod === 3) return <Chip label="CANCELADO" sx={{ width: '100%' }} />;
        return <Chip label="A VENCER" color="primary" sx={{ width: '100%' }} />;
    }

    async function handleSave() {
        if (openLoad) return;
        if (!infoCliente) return AlertInfo({ icon: 'error', title: 'Selecione um cliente' });
        if (!data_vencimento) return AlertInfo({ icon: 'error', title: 'Informe a data de vencimento' });
        if (!moment(data_vencimento).isValid()) return AlertInfo({ icon: 'error', title: 'Data de vencimento invalida' });
        const valorNum = converterMoeda(valor_origem);
        if (isNaN(valorNum) || valorNum <= 0) return AlertInfo({ icon: 'error', title: 'Informe um valor válido' });
        if (tipo_conta === 'CARNET') {
            const parcelasNum = Number(parcelas);
            if (isNaN(parcelasNum) || parcelasNum <= 1) return AlertInfo({ icon: 'error', title: 'Número de parcelas deve ser superior a 1' });
        }
        setOpenLoad(true);
        try {
            const url = tipo_conta === 'CARNET' ? '/createcarnetreceber' : '/createboletoreceber';
            const data: any = { infoCliente, data_vencimento, detalhes: detalhes.toUpperCase() };
            if (tipo_conta === 'CARNET') {
                const parcelasNum = Number(parcelas);
                const valorTotal = String(tipo_valor) === 'PARCELA'
                    ? parseFloat((valorNum * parcelasNum).toFixed(2))
                    : valorNum;
                data.valor_total = valorTotal;
                data.parcelas = parcelasNum;
            } else {
                data.valor_origem = valorNum;
            }
            const response = await api.post(url, data);
            if (response.data.success) {
                const msg = tipo_conta === 'CARNET'
                    ? `Carnê gerado com sucesso! ${response.data.totalParcelas} parcelas criadas.`
                    : 'Boleto gerado com sucesso!';
                const installmentId = response.data.installmentId || '';
                const boletoUrl = response.data.boletoUrl || response.data.invoiceUrl || '';
                AlertInfo({ icon: 'success', title: msg }).then(async () => {
                    if (tipo_conta === 'CARNET' && installmentId) {
                        await abrirCarnetPdf(installmentId);
                    } else if (boletoUrl) {
                        window.open(boletoUrl, '_blank');
                    }
                    setOpen(false);
                    resetForm();
                    reload();
                });
            } else {
                AlertInfo({ icon: 'error', title: response.data.msg || 'Falha ao gerar cobrança' });
            }
        } catch (error) {
            console.log(error);
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function handleReceber() {
        if (openLoad || !contaReceber) return;
        const valorPagoNum = converterMoeda(valor_pago);
        if (!data_pagamento) return AlertInfo({ icon: 'error', title: 'Informe a data do pagamento' });
        if (isNaN(valorPagoNum) || valorPagoNum <= 0) return AlertInfo({ icon: 'error', title: 'Informe o valor recebido' });
        setOpenLoad(true);
        try {
            const response = await api.post(`/recebercontareceber/${contaReceber.id_receber}`, {
                valor_pago: valorPagoNum,
                data_pagamento,
                id_transaction_boleto: contaReceber.id_transaction_boleto,
            });
            if (response.data.success) {
                AlertInfo({ icon: 'success', title: 'Recebimento registrado!' }).then(() => {
                    setOpen(false);
                    setOpenModalPagamento(false);
                    resetForm();
                    reload();
                });
            } else {
                AlertInfo({ icon: 'error', title: response.data.error || 'Falha ao registrar' });
            }
        } catch (error) {
            AlertInfo({ icon: 'error', title: 'Falha na conexão' });
        }
        setOpenLoad(false);
    }

    async function handleCancelar() {
        if (!contaReceber) return;
        AlertConfirm({ title: 'CANCELAR COBRANÇA?', text: 'A cobrança será cancelada no Asaas também. Esta ação não pode ser desfeita.' }).then(async (res) => {
            if (res.isConfirmed) {
                setOpenLoad(true);
                try {
                    const response = await api.post(`/cancelarcontareceber/${contaReceber.id_receber}`, {
                        id_transaction_boleto: contaReceber.id_transaction_boleto,
                    });
                    if (response.data.success) {
                        AlertInfo({ icon: 'success', title: 'Cobrança cancelada' }).then(() => {
                            setOpen(false);
                            resetForm();
                            reload();
                        });
                    } else {
                        AlertInfo({ icon: 'error', title: response.data.error || 'Falha ao cancelar' });
                    }
                } catch (error) {
                    AlertInfo({ icon: 'error', title: 'Falha na conexão' });
                }
                setOpenLoad(false);
            }
        });
    }

    useEffect(() => {
        if (contaReceber && contaReceber.id_receber) {
            setDetalhes(contaReceber.detalhes || '');
            setDataVencimento(formatDate(contaReceber.data_vencimento, "usd"));
            setValorOrigem(displayMoney(contaReceber.valor_origem));
            setTipoConta(contaReceber.tipo_conta_receber || 'BOLETO');
            setValorPago(displayMoney(contaReceber.valor_pago || 0));
            // Buscar parcelas do carnê (só quando mudar de carnê, não ao trocar parcela)
            if (contaReceber.tipo_conta_receber === 'CARNET' && contaReceber.carnet_id_boleto) {
                const jaTemParcelas = parcelasCarnet.length > 0 && parcelasCarnet[0].carnet_id_boleto === contaReceber.carnet_id_boleto;
                if (!jaTemParcelas) {
                    api.get(`/parcelascarnet/${contaReceber.carnet_id_boleto}`)
                        .then(res => { if (Array.isArray(res.data)) setParcelasCarnet(res.data); })
                        .catch(() => setParcelasCarnet([]));
                }
            } else {
                setParcelasCarnet([]);
            }
        }
    }, [contaReceber]);

    const nomeClienteLabel = infoCliente
        ? infoCliente.nome_cliente
        : contaReceber
            ? (contaReceber as any).nome_cliente || `Cliente #${contaReceber.id_cliente_receber}`
            : 'Selecione o cliente';

    const clienteDefinido = !!(infoCliente || contaReceber);
    const isEdicao = !!(contaReceber && contaReceber.id_receber);
    const podeReceberOuCancelar = isEdicao && contaReceber!.situacao_conta !== 1 && contaReceber!.situacao_conta !== 3;

    return (
        <>
            <ListaClientes
                open={openModalBuscaCliente}
                setOpen={setOpenModalBuscaCliente}
                setInfoCliente={setInfoCliente}
            />

            {/* Modal de recebimento */}
            <Modal
                open={openModalPagamento}
                setOpen={setOpenModalPagamento}
                title="Registrar Recebimento"
                maxWidth="xs"
                zIndex={3333}
            >
                <Grid container spacing={1}>
                    <SimpleBackdrop open={openLoad} />
                    <Grid item xs={12}>
                        <InputLabel
                            label="Data do Pagamento"
                            type="date"
                            value={data_pagamento}
                            setState={setDataPagamento}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <InputLabel
                            label="Valor Recebido"
                            value={valor_pago}
                            setState={setValorPago}
                            isMoney
                            isSelectText
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" fullWidth color="success" onClick={handleReceber}>
                            CONFIRMAR RECEBIMENTO
                        </Button>
                    </Grid>
                </Grid>
            </Modal>

            {/* Modal principal */}
            <Modal
                open={open}
                setOpen={setOpen}
                title={isEdicao ? "Conta a Receber" : "Nova Conta a Receber"}
                maxWidth="md"
                zIndex={2222}
                actionClose={resetForm}
            >
                <Grid container spacing={1}>
                    <SimpleBackdrop open={openLoad} />

                    {/* Status e ações para edição */}
                    {isEdicao && (
                        <>
                            <Grid item xs={12} sm={3}>
                                {getSituacaoChip(contaReceber!.situacao_conta)}
                            </Grid>
                            {contaReceber!.situacao_conta === 1 ? (
                                <>
                                    <Grid item xs={12} sm={6} />
                                    <Grid item xs={12} sm={3}>
                                        <Button
                                            variant="contained"
                                            fullWidth
                                            startIcon={<PrintIcon />}
                                            onClick={handleGerarRecibo}
                                            sx={{ bgcolor: "#1565c0", "&:hover": { bgcolor: "#0d47a1" } }}
                                        >
                                            GERAR RECIBO
                                        </Button>
                                    </Grid>
                                </>
                            ) : (
                                <Grid item xs={12} sm={3} />
                            )}
                            {podeReceberOuCancelar && (
                                <>
                                    <Grid item xs={12} sm={3}>
                                        <Button variant="contained" fullWidth color="success"
                                            onClick={() => setOpenModalPagamento(true)}>
                                            RECEBER
                                        </Button>
                                    </Grid>
                                    <Grid item xs={12} sm={3}>
                                        <Button variant="contained" fullWidth color="error" onClick={handleCancelar}>
                                            CANCELAR
                                        </Button>
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    {/* Seleção de cliente */}
                    <Grid item xs={12}>
                        <ContainerTreeItem selectedItems="containerCliente">
                            <CustomTreeItem
                                itemId="containerCliente"
                                label={nomeClienteLabel}
                                onClick={selectCliente}
                                color="#fff"
                                bgColor={clienteDefinido ? theme.palette.success.main : theme.palette.error.main}
                                labelIcon={AccountBoxIcon}
                            />
                        </ContainerTreeItem>
                    </Grid>

                    {/* Campos do formulário */}
                    <Grid item xs={12} sm={4}>
                        <InputLabel
                            label="Data de Vencimento"
                            type="date"
                            value={data_vencimento}
                            setState={setDataVencimento}
                            disabled={isEdicao}
                        />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <InputLabel
                            label={
                                tipo_conta !== 'CARNET' ? 'Valor' :
                                String(tipo_valor) === 'PARCELA' ? 'Valor por Parcela' : 'Valor Total do Carnê'
                            }
                            value={valor_origem}
                            setState={setValorOrigem}
                            isMoney
                            disabled={isEdicao}
                        />
                    </Grid>

                    {!isEdicao && (
                        <>
                            <Grid item xs={12} sm={4}>
                                <SelectSimple
                                    label="Tipo de Cobrança"
                                    value={tipo_conta}
                                    setState={setTipoConta}
                                    options={[
                                        { value: 'BOLETO', label: 'Boleto' },
                                        { value: 'CARNET', label: 'Carnê (Parcelado)' },
                                    ]}
                                />
                            </Grid>
                            {tipo_conta === 'CARNET' && (
                                <>
                                    <Grid item xs={12} sm={4}>
                                        <InputLabel
                                            label="Número de Parcelas"
                                            type="number"
                                            value={String(parcelas)}
                                            setState={setParcelas}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={4}>
                                        <SelectSimple
                                            label="O valor informado é"
                                            value={tipo_valor}
                                            setState={setTipoValor}
                                            options={[
                                                { value: 'TOTAL', label: 'Valor total do carnê' },
                                                { value: 'PARCELA', label: 'Valor de cada parcela' },
                                            ]}
                                        />
                                    </Grid>
                                </>
                            )}
                        </>
                    )}

                    {isEdicao && (
                        <Grid item xs={12} sm={4}>
                            <InputLabel
                                label="Tipo"
                                value={String(tipo_conta)}
                                disabled
                            />
                        </Grid>
                    )}

                    <Grid item xs={12}>
                        <InputLabel
                            label="Descrição / Detalhes"
                            value={detalhes}
                            setState={setDetalhes}
                            isUpper
                            disabled={isEdicao}
                        />
                    </Grid>

                    {/* Links boleto/fatura se existir */}
                    {isEdicao && contaReceber!.invoice_url && (
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open(contaReceber!.invoice_url, '_blank')}
                            >
                                VER FATURA
                            </Button>
                        </Grid>
                    )}
                    {isEdicao && contaReceber!.boleto_url && (
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                fullWidth
                                startIcon={<OpenInNewIcon />}
                                onClick={() => window.open(contaReceber!.boleto_url, '_blank')}
                            >
                                VER BOLETO
                            </Button>
                        </Grid>
                    )}
                    {isEdicao && contaReceber!.tipo_conta_receber === 'CARNET' && contaReceber!.carnet_id_boleto && (
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="outlined"
                                fullWidth
                                color="secondary"
                                startIcon={<OpenInNewIcon />}
                                onClick={() => abrirCarnetPdf(contaReceber!.carnet_id_boleto!)}
                            >
                                VER CARNÊ COMPLETO
                            </Button>
                        </Grid>
                    )}

                    {/* Lista de parcelas do carnê */}
                    {isEdicao && contaReceber!.tipo_conta_receber === 'CARNET' && parcelasCarnet.length > 0 && (
                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.5 }}>
                                <ReceiptLongIcon sx={{ fontSize: 20, color: "#1565c0" }} />
                                <Typography fontSize={14} fontWeight={700} color="#1565c0">
                                    Parcelas do Carnê ({parcelasCarnet.length})
                                </Typography>
                            </Box>
                            <Box sx={{ maxHeight: 260, overflowY: "auto", pr: 0.5 }}>
                                {parcelasCarnet
                                    .sort((a, b) => new Date(a.data_vencimento).getTime() - new Date(b.data_vencimento).getTime())
                                    .map((parcela, idx) => {
                                        const isAtual = parcela.id_receber === contaReceber!.id_receber;
                                        const situacao = Number(parcela.situacao_conta);
                                        const inativa = Number(parcela.conta_receber_ativa) === 0;
                                        return (
                                            <Box
                                                key={parcela.id_receber}
                                                onClick={() => { if (!isAtual) setContaReceber(parcela); }}
                                                sx={{
                                                    display: "flex", alignItems: "center", gap: 1.5,
                                                    py: 0.8, px: 1.5, mb: 0.5, borderRadius: 1,
                                                    bgcolor: isAtual ? "#e3f2fd" : "#fafafa",
                                                    border: "1px solid",
                                                    borderColor: isAtual ? "#90caf9" : "#eeeeee",
                                                    opacity: inativa && !isAtual ? 0.6 : 1,
                                                    cursor: isAtual ? "default" : "pointer",
                                                    transition: "background-color 0.15s",
                                                    "&:hover": !isAtual ? { bgcolor: "#f0f7ff" } : {},
                                                }}
                                            >
                                                <Typography fontSize={12} fontWeight={600} color="text.secondary" sx={{ minWidth: 28 }}>
                                                    {idx + 1}/{parcelasCarnet.length}
                                                </Typography>
                                                <Typography fontSize={13} sx={{ minWidth: 90 }}>
                                                    {formatDate(parcela.data_vencimento)}
                                                </Typography>
                                                <Typography fontSize={13} fontWeight={600} sx={{ flex: 1 }}>
                                                    {displayMoney(parcela.valor_origem)}
                                                </Typography>
                                                {situacao === 1 ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<CheckCircleIcon sx={{ fontSize: 14 }} />}
                                                        label="Recebido"
                                                        color="success"
                                                        sx={{ fontSize: 11, height: 22 }}
                                                    />
                                                ) : situacao === 2 ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                                                        label="Em Atraso"
                                                        color="error"
                                                        sx={{ fontSize: 11, height: 22 }}
                                                    />
                                                ) : situacao === 3 || inativa ? (
                                                    <Chip
                                                        size="small"
                                                        icon={<CancelIcon sx={{ fontSize: 14 }} />}
                                                        label="Cancelado"
                                                        sx={{ fontSize: 11, height: 22, bgcolor: "#eeeeee", color: "#757575" }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        size="small"
                                                        label="A Vencer"
                                                        color="primary"
                                                        variant="outlined"
                                                        sx={{ fontSize: 11, height: 22 }}
                                                    />
                                                )}
                                                {isAtual && (
                                                    <Chip
                                                        size="small"
                                                        label="Atual"
                                                        sx={{ fontSize: 10, height: 18, bgcolor: "#1565c0", color: "#fff" }}
                                                    />
                                                )}
                                            </Box>
                                        );
                                    })}
                            </Box>
                        </Grid>
                    )}

                    {/* Botão salvar - apenas para nova conta */}
                    {!isEdicao && (
                        <>
                            <Grid item xs={false} sm={4} />
                            <Grid item xs={12} sm={4}>
                                <Button
                                    variant="contained"
                                    fullWidth
                                    color="primary"
                                    onClick={handleSave}
                                >
                                    GERAR COBRANÇA
                                </Button>
                            </Grid>
                            <Grid item xs={false} sm={4} />
                        </>
                    )}
                </Grid>
            </Modal>
        </>
    );
}
